import React from 'react';
import { clsx } from 'clsx';

export interface SimpleTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  containerClassName?: string;
}

/**
 * SimpleTextarea - Textarea component without Formik dependency
 */
export function SimpleTextarea({
  label,
  error,
  helperText,
  containerClassName,
  className,
  required,
  disabled,
  id = '',
  ...props
}: SimpleTextareaProps) {
  const hasError = !!error;
  const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={clsx('w-full', containerClassName)}>
      {label && (
        <label
          htmlFor={textareaId}
          className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <textarea
        id={textareaId}
        {...props}
        disabled={disabled}
        className={clsx(
          'block w-full rounded-lg border px-4 py-2.5 text-base transition-colors duration-200',
          'text-gray-900 dark:text-white',
          'placeholder-gray-400 dark:placeholder-gray-500',
          'bg-white dark:bg-gray-800',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          hasError
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600',
          disabled && 'cursor-not-allowed bg-gray-50 opacity-50 dark:bg-gray-900',
          className
        )}
      />

      {hasError && <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {helperText && !hasError && (
        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </div>
  );
}
